import { useContext, useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import HeadingSection from "./HeadingSection";
import {
  Context,
  getSearchPayload,
  getSearchResultsWithPagination,
  payloadToUrl,
  payloadToUrlForBanks,
} from "services";
import { useView } from "./../../../../app/components/propertiesPage/context/ViewContext";
import FilterSectionWeb from "./FilterSectionWeb";
import MobileFilterSection from "./MobileFilterSection";
import useLocation from "../../../hoc/useLocation";
import LazyComponent from "../../../hoc/LazyComponent";

interface V2PropertiesComponentProps {
  cities: any;
  defaultLocation: any;
}

const bankName = process.env.NEXT_PUBLIC_BANK;

export default function V2PropertiesComponent({
  defaultLocation,
  cities,
}: V2PropertiesComponentProps) {
  const requestIdRef = useRef(0);
  const { city } = useLocation(defaultLocation, cities);
  const { searchForm }: any = useContext(Context);
  const router = useRouter();
  const [allPropertiesData, setAllPropertiesData] = useState<any>([]);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [noMoreData, setNoMoreData] = useState(false);
  const { currentView, page, setPage } = useView();

  const handleClearFilters = (ClearFiltersData: any) => {
    // Reset all filter fields
    searchForm.setField("asset_type", "");
    searchForm.setField("sub_asset_type", "");
    searchForm.setField("state", "");
    if (!ClearFiltersData || ClearFiltersData.type !== "all_properties") {
      searchForm.setField("city", city || "");
    }
    searchForm.setField("locality", "");
    searchForm.setField("reserve_price", "");
    searchForm.setField("institutes", "");
    searchForm.setField("filter_by", "");
    searchForm.setField("sort_by", "");
    searchForm.setField("auction_start_date_unix_timestamp", "");
    searchForm.setField("listing_id_query", "");
    searchForm.setField("ab_verified", "");
    if (bankName) {
      payloadToUrlForBanks(router, searchForm.value);
    } else {
      payloadToUrl(router, searchForm.value);
    }
  };

  // Debounce function to limit frequency of function calls
  const debounce = (func: any, delay: number): any => {
    let timeoutId: any;
    return (...args: any[]): void => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Main function to fetch and update search results
  const fetchSearchResults = async (pageToFetch: number) => {
    setIsFetchingNextPage(true);
    const currentRequestId = requestIdRef.current;
    const typeSensePayload = getSearchPayload(searchForm, router, pageToFetch);
    try {
      const response: any = await getSearchResultsWithPagination(
        {
          searches: typeSensePayload,
        },
        48,
        pageToFetch,
      );
      const searchResults = response?.results[0]?.hits?.map(
        (value: any) => value.document,
      );
      if (currentRequestId < requestIdRef.current) {
        return;
      } else if (currentRequestId > requestIdRef.current) {
        setAllPropertiesData([]);
        return;
      }
      searchForm.setField("total", response?.results[0]?.found);
      if (searchResults && searchResults.length > 0) {
        const previousScrollTop =
          document.documentElement.scrollTop || document.body.scrollTop;
        setAllPropertiesData((prevData: any) => [
          ...prevData,
          ...searchResults,
        ]);
        setPage(pageToFetch);
        setTimeout(() => {
          window.scrollTo(0, previousScrollTop);
        }, 0);

        if (searchResults.length < 48) {
          setNoMoreData(true);
        }
      } else {
        setNoMoreData(true);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setIsFetchingNextPage(false);
    }
  };

  useEffect(() => {
    setAllPropertiesData([]);
    requestIdRef.current += 1;
    setPage(1);
    setNoMoreData(false);
    fetchSearchResults(1);
    if (bankName) {
      payloadToUrlForBanks(router, searchForm.value);
    } else {
      payloadToUrl(router, searchForm.value);
    }
  }, [
    searchForm.value.asset_type.value,
    JSON.stringify(searchForm.value.sub_asset_type),
    searchForm.value.state.value,
    searchForm.value.institutes,
    searchForm.value.city.value,
    searchForm.value.locality.length,
    searchForm.value.reserve_price,
    searchForm.value.auction_start_date_unix_timestamp,
    searchForm.value.filter_by.value,
    searchForm.value.sort_by.value,
    searchForm.value.listing_id_query,
    searchForm.value.ab_verified,
    bankName,
  ]);

  // Effect to handle infinite scrolling
  useEffect(() => {
    if (currentView === "grid") {
      const handleScroll = debounce(() => {
        const scrollTop =
          document.documentElement.scrollTop || document.body.scrollTop;

        const scrollHeight =
          document.documentElement.scrollHeight || document.body.scrollHeight;

        const clientHeight =
          document.documentElement.clientHeight || window.innerHeight;

        const scrolledToBottom =
          Math.ceil(scrollTop + clientHeight) >= scrollHeight - 200;

        if (scrolledToBottom) {
          loadMoreData();
        }
      }, 300);

      window.addEventListener("scroll", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [currentView, page, isFetchingNextPage, noMoreData]);

  const loadMoreData = () => {
    if (!isFetchingNextPage && !noMoreData) {
      fetchSearchResults(page + 1);
    }
  };

  return (
    <>
      <FilterSectionWeb handleClearFilters={handleClearFilters} />
      <div className="relative bg-white  px-2 ">
        <HeadingSection
          allPropertiesData={allPropertiesData}
          propertyCount={searchForm.value.total}
          isFetchingNextPage={isFetchingNextPage}
          handleClearFilters={handleClearFilters}
        />
        {noMoreData && (
          <LazyComponent
            importFunc={() => import("app-v2").then((mod) => mod.Footer)}
          />
        )}
      </div>
      <div className="fixed bottom-0 left-0 w-full bg-white lg:hidden border-t-[1px]">
        <MobileFilterSection handleClearFilters={handleClearFilters} />
      </div>
    </>
  );
}
